/**
 * strings.ts — common scalar string schemas.
 *
 * DAT-114 references:
 *   - all dates in ISO 8601 format
 *   - all email addresses RFC 5322 compliant
 *   - all text fields sanitised against XSS (lives at the route handler /
 *     React rendering layer — schemas just enforce length + format here)
 */

import { z } from 'zod';

/** Calendar date in `YYYY-MM-DD` (ISO 8601 — date only). */
export const IsoDate = z
  .string()
  .regex(
    /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/,
    'Expected ISO 8601 date (YYYY-MM-DD)',
  )
  .describe('Calendar date (ISO 8601, YYYY-MM-DD).');
export type IsoDate = z.infer<typeof IsoDate>;

/** ISO 8601 date-time with timezone offset (e.g. `2026-05-08T14:30:00Z`). */
export const IsoDateTime = z
  .string()
  .datetime({ offset: true })
  .describe('ISO 8601 date-time with offset (UTC stored, displayed per user.timezone).');
export type IsoDateTime = z.infer<typeof IsoDateTime>;

/** RFC 5322 email — Zod's built-in regex is good enough for our use. */
export const EmailString = z
  .string()
  .email('Expected RFC 5322 email')
  .max(255)
  .describe('User email address (RFC 5322).');
export type EmailString = z.infer<typeof EmailString>;

/** IANA timezone identifier — e.g. `Europe/Berlin`. */
export const IanaTimezone = z
  .string()
  .min(1)
  .max(64)
  // Cheap shape check — the canonical IANA list is too long to inline.
  // Server-side acceptance happens via Intl.supportedValuesOf if needed.
  .regex(/^[A-Za-z_+\-/]+$/, 'Expected IANA timezone identifier')
  .describe('IANA timezone identifier (e.g. Europe/Berlin).');
export type IanaTimezone = z.infer<typeof IanaTimezone>;

/** UI language — only DE/EN supported through R3 (CLAUDE.md §12). */
export const UiLanguage = z
  .enum(['de', 'en'])
  .describe('UI language. Only de/en supported through R3.');
export type UiLanguage = z.infer<typeof UiLanguage>;
